import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';
import {useDispatch} from 'react-redux';
import _ from 'underscore';

import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import BenchlingConfig from 'BenchlingConfig';
import {Tag} from 'folder-items/FileConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST, graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {LoadingFileScreen} from 'nav/full-screen/LoadingFullScreen';
import {AllSourcesQuery} from 'nav/graphql/__generated/AllSourcesQuery';
import {ALL_SOURCES_QUERY} from 'nav/graphql/AllSources';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import notifications from 'notifications';
import {BenchlingDispatch} from 'redux-stores/Store';
import EditableRequestAssignees from 'requests/assignees/EditableRequestAssignees';
import prepareAssigneesForServer from 'requests/assignees/prepareAssigneesForServer';
import RequestAssignees from 'requests/assignees/RequestAssignees';
import ExecuteButton from 'requests/execution/ExecuteButton';
import ExecuteRequestFlow from 'requests/execution/ExecuteRequestFlow';
import {RequestSampleGroupsFragment, RequestTasksFragment} from 'requests/graphql/Request';
import * as InputReduxActions from 'requests/inputs/InputReduxActions';
import CopyRequestButton from 'requests/request-view/CopyRequestButton';
import {RequestAssignee, RequestDetailsRequestGraphQL, RequestStatus} from 'requests/RequestConstants';
import RequestStatusDropdown from 'requests/RequestStatusDropdown';
import DraftableRequestSampleTable from 'requests/samples-table/DraftableRequestSampleTable';
import AddTaskSchemaDropdown from 'requests/tasks/AddTaskSchemaDropdown';
import CreateRequestTaskModal, {CreateRequestTaskModalProps} from 'requests/tasks/CreateRequestTaskModal';
import {makeRequestTaskGraphQL} from 'requests/tasks/MakeRequestTask';
import prepareTaskSampleGroupAssociationsForServer from 'requests/tasks/prepareTaskSampleGroupAssociationsForServer';
import RequestTaskCard from 'requests/tasks/RequestTaskCard';
import {ServerRequestTask} from 'requests/tasks/RequestTaskConstants';
import * as RequestTaskReduxActions from 'requests/tasks/RequestTaskReduxActions';
import SchemaTagInputs from 'schemas/tags/SchemaTagInputs';
import * as TagUtil from 'schemas/tags/TagUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import Avatar from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {TooltipPlacement, TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import EditableDateField from 'ui-library/EditableDateField';
import * as Headers from 'ui-library/Headers';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import legacyRunModal from 'ui-library/legacyRunModal';
import {UnconnectedProjectSelector} from 'ui-library/ProjectSelector';
import {isValidDate} from 'util/DateUtil';
import * as modals from 'util/Modals';
import {SampleGroupIdsByRequestTaskIdMap} from 'workflows/samples/getSampleGroupIdsByRequestTaskId';
import {SampleGroupUpdate} from 'workflows/WorkflowConstants';

import {NavStoreSourceGraphQL} from '../../../graphql-fragments/nav/__generated/NavStoreSourceGraphQL';
import {RequestTaskSchemaGraphQL} from '../../../graphql-fragments/requests/schemas/__generated/RequestTaskSchemaGraphQL';
import {RequestDetailsQuery, RequestDetailsQueryVariables} from './__generated/RequestDetailsQuery';

// Context value is hoisted to module scope to avoid re-renders
const noFormulasContext = {showFormulas: false, isConfig: false};

export interface RequestDetailsProps {
  request: RequestDetailsRequestGraphQL;
  sources: Array<NavStoreSourceGraphQL>;
}

export interface ActionProps {
  deleteRequestTask: (requestTaskId: string) => void;
  updateRequest: (requestId: string, data: InputReduxActions.UpdateRequestArgs) => void;
  updateRequestSampleGroups: (args: {
    requestId: string;
    sampleGroupIdsByRequestTaskId: SampleGroupIdsByRequestTaskIdMap;
    sampleGroups: Array<SampleGroupUpdate>;
  }) => Promise<void>;
  updateRequestTask: (newRequestTask: ServerRequestTask) => void;
}

export class RequestDetails extends React.Component<RequestDetailsProps & ActionProps> {
  _handleUpdateAssignees = (assignees: Array<RequestAssignee>): void => {
    this.props.updateRequest(this.props.request.id, {assignees: prepareAssigneesForServer(assignees)});
  };

  _handleUpdateScheduledOnDate = (scheduledOn: string | null): void => {
    if (isValidDate({textValue: scheduledOn})) {
      this.props.updateRequest(this.props.request.id, {scheduledOn: scheduledOn ?? ''});
    } else {
      notifications.error('Invalid date.');
    }
  };

  _handleUpdateProject = (projectId: string): void => {
    this.props.updateRequest(this.props.request.id, {projectId});
  };

  _handleSaveSampleGroups = async (args: {
    sampleGroupIdsByRequestTaskId: {[taskCid: string]: Array<string>};
    sampleGroupsForServer: Array<SampleGroupUpdate>;
  }): Promise<void> => {
    await this.props.updateRequestSampleGroups({
      requestId: this.props.request.id,
      sampleGroupIdsByRequestTaskId: prepareTaskSampleGroupAssociationsForServer({
        requestTasks: this.props.request.tasks,
        sampleGroupIdsByRequestTaskId: args.sampleGroupIdsByRequestTaskId,
        sampleGroupsForServer: args.sampleGroupsForServer,
      }),
      sampleGroups: args.sampleGroupsForServer,
    });
  };

  _handleUpdateTags = (tags: Array<Tag>): void => {
    const tagsToSet = TagUtil.getTagsToSet(tags);
    this.props.updateRequest(this.props.request.id, {tags: tagsToSet});
  };

  _handleOpenAddRequestTaskModal = async (requestTaskSchema: RequestTaskSchemaGraphQL): Promise<void> => {
    await legacyRunModal<Omit<CreateRequestTaskModalProps, 'createRequestTask'>>(CreateRequestTaskModal, {
      initialRequestTask: makeRequestTaskGraphQL(requestTaskSchema),
      requestId: this.props.request.id,
    });
  };

  _handleDeleteRequestTask(requestTaskId: string): void {
    modals
      .confirm(
        'Delete task',
        'Are you sure you want to delete the task? This action is not reversible.',
        'danger'
      )
      .done(() => {
        this.props.deleteRequestTask(requestTaskId);
      });
  }

  _handleOpenExecuteRequestFlow = (): void => {
    LegacyModalContainer.open(
      ExecuteRequestFlow,
      <ExecuteRequestFlow
        requests={[graphQLToREST('Request', this.props.request)]}
        initialFilters={[]}
        requestSchemasByRequestId={{
          [this.props.request.id]: graphQLToREST('RequestSchema', this.props.request.schema),
        }}
        onRequestClose={() => {
          LegacyModalContainer.close(ExecuteRequestFlow);
        }}
      />
    );
  };

  _renderScheduledOn(): JSX.Element {
    return (
      <div>
        <Headers.Title>Scheduled on</Headers.Title>
        <EditableDateField
          onChange={this._handleUpdateScheduledOnDate}
          value={this.props.request.scheduledOn}
          isDisabled={!this._isWritable()}
          placeholder={null}
        />
      </div>
    );
  }

  _isWritable(): boolean {
    return this.props.request.permissions.writable;
  }

  _canExecuteRequest(): boolean {
    return Boolean(this.props.request.permissions.canExecuteRequest);
  }

  _renderAssignedTo(): JSX.Element {
    return (
      <div>
        <Headers.Title>Assigned to</Headers.Title>
        {this._isWritable() ? (
          <EditableRequestAssignees
            assignees={this.props.request.assigneesJson}
            onUpdateAssignees={this._handleUpdateAssignees}
            organizationId={this.props.request.schema.organizationJson.id}
            shouldChangeBeConfirmed={true}
          />
        ) : (
          <RequestAssignees assignees={this.props.request.assigneesJson} />
        )}
      </div>
    );
  }

  _renderExecuteButton({
    buttonStyle,
    className,
  }: {
    buttonStyle: ButtonStyle;
    className: string;
  }): JSX.Element | null {
    if (!this._isWritable) {
      return null;
    }
    let disabled = false;
    let tooltip = null;
    if (!this._canExecuteRequest()) {
      disabled = true;
      tooltip = 'Requires execute permission';
    } else if (this.props.request.schema.sampleGroupSchema && this.props.request.sampleGroups.length === 0) {
      disabled = true;
      tooltip = 'No samples specified';
    }
    return (
      <ExecuteButton
        buttonStyle={buttonStyle}
        className={className}
        disabled={disabled}
        onClick={this._handleOpenExecuteRequestFlow}
        tooltipTitle={tooltip}
      />
    );
  }

  _renderExportDropdown(): JSX.Element {
    return (
      <TooltipWrapper tooltipTitle={EXPORT_AUDIT_LOG_DISPLAY_STRING} tooltipPlacement={TooltipPlacement.TOP}>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.SMALL}
          onClick={() => {
            const selectedIds = [this.props.request.id];
            exportAuditLogs(selectedIds);
          }}
          icon="audit-trail"
          dataTestAttrs={{component: 'RequestDetails', element: 'exportAuditLog'}}
          className="u-mini-spacing-left"
        />
      </TooltipWrapper>
    );
  }

  _renderRequestButtons(): JSX.Element {
    return (
      <div className="u-flex-display">
        <RequestStatusDropdown
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          isDisabled={!this._isWritable()}
          onUpdateRequestStatus={(requestStatus: RequestStatus) => {
            this.props.updateRequest(this.props.request.id, {requestStatus});
          }}
          requestStatus={this.props.request.requestStatus}
        />
        <CopyRequestButton request={this.props.request} />
        {this._renderExecuteButton({buttonStyle: ButtonStyle.SECONDARY, className: 'u-mini-spacing-left'})}
        {this._renderExportDropdown()}
      </div>
    );
  }

  override render(): JSX.Element {
    const requestTaskSchemasById = _.indexBy(
      this.props.request.schema.requestTaskSchemas,
      (schema) => schema.id
    );

    return (
      <div className="react-tab-container">
        <div className="requestView">
          <div>
            <div className="u-lr-split">
              <Headers.Header1>
                {this.props.request.schema.name} {this.props.request.name}
              </Headers.Header1>
              {this._renderRequestButtons()}
            </div>
          </div>
          <div className="u-spacing-top">
            <Row bufferRowReset={true}>
              <Col xs={6}>
                <Headers.Title>Project</Headers.Title>
                <UnconnectedProjectSelector
                  onProjectSelected={this._handleUpdateProject}
                  sources={graphQLArrayToREST('NavStoreSource', this.props.sources)}
                  selectedProjectId={this.props.request.project.id}
                  readOnly={!this._isWritable()}
                  enableRedesign={true}
                  areSourcesLoading={false}
                />
              </Col>
              <Col xs={6}>
                <Headers.Title>
                  {BenchlingConfig.REQUESTS_ENABLE_REQUESTOR ? 'Requestor' : 'Creator'}
                </Headers.Title>
                <div>
                  <Avatar
                    className="u-mini-spacing-right"
                    size={AvatarSize.XXSMALL}
                    entity={
                      BenchlingConfig.REQUESTS_ENABLE_REQUESTOR
                        ? this.props.request.requestorJson
                        : this.props.request.creator
                    }
                  />
                  {BenchlingConfig.REQUESTS_ENABLE_REQUESTOR
                    ? this.props.request.requestorJson.name
                    : this.props.request.creator.name}
                </div>
              </Col>
            </Row>
            <Row bufferRowReset={true} className="u-spacing-top">
              <Col xs={6}>{this._renderScheduledOn()}</Col>
              <Col xs={6}>{this._renderAssignedTo()}</Col>
            </Row>
            <div className="u-spacing-top">
              <SchemaTagInputs
                className="bufferRowReset u-small-spacing-top"
                itemLinkAllowed={true}
                isWritable={this._isWritable()}
                onUpdateTags={this._handleUpdateTags}
                tags={this.props.request.tagsJson}
              />
            </div>
            {this.props.request.schema.requestTaskSchemas.length > 0 && (
              <div className="u-spacing-top">
                <Headers.Title>
                  Tasks
                  {this._isWritable() && (
                    <AddTaskSchemaDropdown
                      requestTaskSchemasById={requestTaskSchemasById}
                      requestTaskSchemaIds={this.props.request.schema.requestTaskSchemas.map(({id}) => id)}
                      onSelectSchema={this._handleOpenAddRequestTaskModal}
                    />
                  )}
                </Headers.Title>
                {this.props.request.tasks.map((requestTask) => (
                  <div
                    key={requestTask.id}
                    className="u-tiny-spacing-top"
                    {...getTestProps({
                      component: 'RequestDetails',
                      element: 'taskCard',
                      key: requestTaskSchemasById[requestTask.schema.id].name,
                    })}
                  >
                    <RequestTaskCard
                      initiallyExpanded={false}
                      isCreating={false}
                      isWritable={this._isWritable()}
                      requestTask={requestTask}
                      requestTaskSchema={requestTaskSchemasById[requestTask.schema.id]}
                      onRemove={() => {
                        this._handleDeleteRequestTask(requestTask.id);
                      }}
                      onUpdate={this.props.updateRequestTask}
                    />
                  </div>
                ))}
              </div>
            )}
            {this.props.request.schema.sampleGroupSchema && (
              <div className="u-spacing-top">
                <EditorPropertiesContext.Provider value={noFormulasContext}>
                  <DraftableRequestSampleTable
                    isWritable={this._isWritable()}
                    request={this.props.request}
                    requestTaskSchemasById={requestTaskSchemasById}
                    onSaveSampleGroups={this._handleSaveSampleGroups}
                  />
                </EditorPropertiesContext.Provider>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export const REQUEST_DETAILS_QUERY: DocumentNode = gql`
  query RequestDetailsQuery($requestId: StrictString!) @engComponent(name: REQUESTS) {
    request(id: $requestId) @audit(msg: "Opened request {name}") {
      ... on RequestNode {
        name # For auditing
        ...RequestGraphQL
        schema {
          ...RequestSchemaGraphQL
          requestTaskSchemas {
            ...RequestTaskSchemaGraphQL
          }
        }
        fulfillments {
          ...RequestFulfillmentGraphQL
        }
        ...RequestTasks
        ...RequestSampleGroups
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('Request').fragment}
  ${getFragmentWrapper('RequestFulfillment').fragment}
  ${getFragmentWrapper('RequestSchema').fragment}
  ${getFragmentWrapper('RequestTask').fragment}
  ${getFragmentWrapper('RequestTaskSchema').fragment}
  ${RequestTasksFragment.fragment}
  ${RequestSampleGroupsFragment.fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function mapDispatchToProps(dispatch: BenchlingDispatch): ActionProps {
  return {
    deleteRequestTask: (requestTaskId: string) => {
      dispatch(RequestTaskReduxActions.deleteRequestTask(requestTaskId));
    },
    updateRequest: (requestId: string, data: InputReduxActions.UpdateRequestArgs) => {
      dispatch(InputReduxActions.updateRequest(requestId, data));
    },
    updateRequestSampleGroups: async (args: {
      requestId: string;
      sampleGroupIdsByRequestTaskId: {[taskId: string]: Array<string>};
      sampleGroups: Array<SampleGroupUpdate>;
    }) => {
      await dispatch(InputReduxActions.updateRequestSampleGroups(args));
    },
    updateRequestTask: (newRequestTask: ServerRequestTask) => {
      dispatch(RequestTaskReduxActions.updateRequestTask(newRequestTask));
    },
  };
}

export default function QueryingRequestDetails(props: {requestId: string}): JSX.Element | null {
  const dispatch = useDispatch<BenchlingDispatch>();
  const {deleteRequestTask, updateRequest, updateRequestSampleGroups, updateRequestTask} =
    mapDispatchToProps(dispatch);

  // We force fetch request data but don't need to do that with the list of sources. Instead, split sources
  // into a separate query for better performance (often they'll be served from cache).
  const allSourcesResult = useBenchlingQuery<AllSourcesQuery>(ALL_SOURCES_QUERY);
  const requestDetailsResult = useBenchlingQuery<RequestDetailsQuery, RequestDetailsQueryVariables>(
    REQUEST_DETAILS_QUERY,
    {variables: {requestId: props.requestId}, renderCachedDataWhenRefetching: true}
  );

  if (
    allSourcesResult.type === QueryResultType.PENDING ||
    requestDetailsResult.type === QueryResultType.PENDING
  ) {
    return <LoadingFileScreen />;
  } else if (
    allSourcesResult.type === QueryResultType.SUCCESS &&
    requestDetailsResult.type === QueryResultType.SUCCESS
  ) {
    if (isInaccessible(requestDetailsResult.data.request)) {
      return (
        <WorkspaceItemInaccessibleGuard
          inaccessibleInfo={requestDetailsResult.data.request}
          modelType="request"
        />
      );
    } else {
      return (
        <RequestDetails
          request={requestDetailsResult.data.request}
          sources={allSourcesResult.data.sources}
          deleteRequestTask={deleteRequestTask}
          updateRequest={updateRequest}
          updateRequestSampleGroups={updateRequestSampleGroups}
          updateRequestTask={updateRequestTask}
        />
      );
    }
  } else {
    notifications.error('An unexpected error occured when loading request details.');
    return null;
  }
}
